import React, { useState } from 'react';
import { z } from 'zod';
import { observer } from 'src/utils/mobx-react';
import { useAppStateContext } from 'src/AppState/AppState';
import { ComplianceAuditState, DisplayNameStatus, type DisplayNameResultType } from './ComplianceAudit.state';
import { IndependentTooltip } from 'src/components/Tooltip/IndependentTooltip';
import { LoadingV2 } from 'src/components/Loading/LoadingV2';
import { Alert } from 'src/domains/common/components/alert/alert';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from 'src/domains/common/components/table/table';
import { cn } from 'src/domains/common/components/utils';
import { IconButton } from 'src/domains/common/components/button/icon-button';
import { Button } from 'src/domains/common/components/button/button';
import { observable, action, makeObservable } from 'mobx';
import { DateTime } from 'src_common/utils/time/time';
import { AuditLogRowType, ComplianceAuditQueryType, type ComplianceAuditViewType } from './ComplianceAudit.store';
import { AuditPanelWrapper } from 'src/components/ComplianceAudits/AuditPanel.style';
import { AuditHeader } from 'src/components/ComplianceAudits/AuditHeader';

class TableState {
    @observable public expandedRows: Set<number> = new Set();

    public constructor() {
        makeObservable(this);
    }

    @action public toggleRowExpansion = (rowId: number): void => {
        if (this.expandedRows.has(rowId)) {
            this.expandedRows.delete(rowId);
        } else {
            this.expandedRows.add(rowId);
        }
    };

    public isExpanded = (rowId: number): boolean => {
        return this.expandedRows.has(rowId);
    };
}

const recordSchema = z.record(z.string(), z.unknown());

const isRecord = (value: unknown): value is Record<string, unknown> => {
    return recordSchema.safeParse(value).success;
};

const formatJsonRecord = (rawData: unknown): React.ReactNode => {
    const data = isRecord(rawData) ? rawData : undefined;

    if (data === undefined || Object.keys(data).length === 0) {
        return <span className='text-muted-foreground'>No data</span>;
    }

    return (
        <div className='grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm'>
            <div className='font-bold text-muted-foreground'>Field</div>
            <div className='font-bold text-muted-foreground'>Value</div>
            {Object.entries(data).map(([key, value]) => (
                <React.Fragment key={key}>
                    <div className='font-medium'>{key}</div>
                    <div className='font-mono text-xs break-all'>
                        {value === undefined ? '-' : JSON.stringify(value)}
                    </div>
                </React.Fragment>
            ))}
        </div>
    );
};

const formatJsonDiff = (rawBefore: unknown, rawAfter: unknown, colsChanged: string[] | undefined): React.ReactNode => {
    const before = isRecord(rawBefore) ? rawBefore : undefined;
    const after = isRecord(rawAfter) ? rawAfter : undefined;
    const allKeys = new Set<string>();
    if (before !== undefined) {
        for (const key of Object.keys(before)) {
            allKeys.add(key);
        }
    }
    if (after !== undefined) {
        for (const key of Object.keys(after)) {
            allKeys.add(key);
        }
    }

    if (allKeys.size === 0) {
        return <span className='text-muted-foreground'>No data</span>;
    }

    const changedSet = new Set(colsChanged ?? []);

    return (
        <div className='grid grid-cols-[auto_1fr_1fr] gap-x-4 gap-y-1 text-sm'>
            <div className='font-bold text-muted-foreground'>Field</div>
            <div className='font-bold text-muted-foreground'>Before</div>
            <div className='font-bold text-muted-foreground'>After</div>
            {Array.from(allKeys).map((key) => {
                const isChanged = changedSet.has(key);
                const beforeVal = before?.[key];
                const afterVal = after?.[key];

                return (
                    <React.Fragment key={key}>
                        <div className={cn('font-medium', isChanged && 'text-amber-600')}>{key}</div>
                        <div
                            className={cn(
                                'font-mono text-xs break-all',
                                isChanged && 'bg-red-50 text-red-700 px-1 rounded'
                            )}
                        >
                            {beforeVal === undefined ? '-' : JSON.stringify(beforeVal)}
                        </div>
                        <div
                            className={cn(
                                'font-mono text-xs break-all',
                                isChanged && 'bg-green-50 text-green-700 px-1 rounded'
                            )}
                        >
                            {afterVal === undefined ? '-' : JSON.stringify(afterVal)}
                        </div>
                    </React.Fragment>
                );
            })}
        </div>
    );
};

const renderWithLoading = (value: DisplayNameResultType): React.ReactNode => {
    if (value === DisplayNameStatus.LOADING) {
        return <div className='w-4 h-4 border-2 border-dashed rounded-full animate-spin border-foreground' />;
    }
    return value;
};

const CUSTOMER_ID_REGEX = /\w+:customer:\d+/g;

const renderReasonWithTooltips = (text: string, state: ComplianceAuditState): React.ReactNode => {
    const matches = [...text.matchAll(CUSTOMER_ID_REGEX)];

    if (matches.length === 0) return text;

    if (matches.some(([id]) => state.getActorDisplayName(id) === DisplayNameStatus.LOADING)) {
        return renderWithLoading(DisplayNameStatus.LOADING);
    }

    const parts = text.split(CUSTOMER_ID_REGEX);

    return parts.reduce<React.ReactNode[]>((acc, part, i) => {
        if (part !== '') acc.push(part);
        const match = matches[i];
        if (match !== undefined) {
            acc.push(
                <IndependentTooltip
                    key={match.index}
                    content={state.getCustomerTooltipLabel(match[0])}
                >
                    <span>{state.getActorDisplayName(match[0])}</span>
                </IndependentTooltip>
            );
        }
        return acc;
    }, []);
};

type ColumnType = 'date' | 'operation' | 'actor' | 'entry_point' | 'reason';
type ViewColumnsType = Record<ComplianceAuditViewType, Array<ColumnType>>;

const AUDIT_COLUMNS: ViewColumnsType = {
    SourceOfFunds: ['date', 'operation', 'actor', 'entry_point', 'reason'],
    SelfExclusion: ['date', 'operation', 'actor', 'entry_point', 'reason'],
    Gamstop: ['date', 'operation', 'entry_point', 'reason'],
    GamstopRequestHistory: ['date', 'operation', 'entry_point', 'reason'],
};

const FEATURE_AUDIT_TITLES: Record<ComplianceAuditViewType, string> = {
    SourceOfFunds: 'Source of Funds Audit',
    SelfExclusion: 'SelfExclusion Audit',
    Gamstop: 'Gamstop Status Audit',
    GamstopRequestHistory: 'Gamstop Request History',
};

interface ComplianceAuditPropsType {
    query: ComplianceAuditQueryType;
    columns: Array<ColumnType>;
}

const ComplianceAuditInner = observer('ComplianceAuditInner', ({ query, columns }: ComplianceAuditPropsType) => {
    const { common, models } = useAppStateContext();

    const [state] = useState(() => new ComplianceAuditState(query, common, models));
    const [tableState] = useState(() => new TableState());

    const auditLogs = state.store.auditLogs;
    const columnNumber = columns.length + 1;
    const feature = query.feature;

    return (
        <AuditPanelWrapper>
            <AuditHeader
                title={FEATURE_AUDIT_TITLES[feature]}
                dataTest='page-title'
                from={state.store.from}
                to={state.store.to}
                onChangeFrom={state.store.handleChangeFromDate}
                onChangeTo={state.store.handleChangeToDate}
            />

            {auditLogs === 'error' ? (
                <Alert
                    title='Error Occurred'
                    variant='destructive'
                    icon='shield'
                >
                    We cannot retrieve the audit data. Please try again.
                </Alert>
            ) : auditLogs === 'loading' ? (
                <LoadingV2
                    dataTest='loading'
                    title='Loading audit data...'
                />
            ) : auditLogs.length === 0 ? (
                <Alert
                    title='No Audit Records'
                    variant='default'
                    icon='info'
                >
                    {query.kind === 'config'
                        ? 'No audit records found for this configuration.'
                        : 'No audit records found for this customer.'}
                </Alert>
            ) : (
                <div className='relative w-full flex-1 min-h-0 overflow-auto border border-solid rounded-md border-border'>
                    <Table className='table-fixed'>
                        <TableHeader className='sticky top-0 bg-white z-[1] shadow-b-shadow'>
                            <TableRow className='!border-b-0'>
                                <TableHead className='!px-2 h-14 w-12'>&nbsp;</TableHead>
                                {columns.includes('date') && (
                                    <TableHead className='py-0 h-14'>
                                        <div className='cursor-pointer flex items-center gap-2.5'>Date</div>
                                    </TableHead>
                                )}
                                {columns.includes('operation') && (
                                    <TableHead className='py-0 h-14'>
                                        <div className='cursor-pointer flex items-center gap-2.5'>Operation</div>
                                    </TableHead>
                                )}
                                {columns.includes('actor') && (
                                    <TableHead className='py-0 h-14'>
                                        <div className='cursor-pointer flex items-center gap-2.5'>Actor</div>
                                    </TableHead>
                                )}
                                {columns.includes('entry_point') && (
                                    <TableHead className='py-0 h-14'>
                                        <div className='cursor-pointer flex items-center gap-2.5'>Entry point</div>
                                    </TableHead>
                                )}
                                {columns.includes('reason') && (
                                    <TableHead className='py-0 h-14'>
                                        <div className='cursor-pointer flex items-center gap-2.5'>Reason</div>
                                    </TableHead>
                                )}
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {auditLogs.map((row: AuditLogRowType, index: number) => {
                                const isExpanded = tableState.isExpanded(row.id);
                                const rowClass = index % 2 === 0 ? 'bg-white' : 'bg-muted/50';
                                const hasDetails = typeof row.before === 'object' || typeof row.after === 'object';
                                return (
                                    <React.Fragment key={row.id}>
                                        <TableRow
                                            className={cn(
                                                rowClass,
                                                'data-[state=selected]:bg-muted border-border border-solid border-t-0 border-l-0 border-r-0'
                                            )}
                                            data-test={`audit-row-${row.id}`}
                                        >
                                            <TableCell className='py-0 !px-2 h-14'>
                                                <IconButton
                                                    className='no-hover'
                                                    variant='ghost'
                                                    size='default'
                                                    onClick={() => tableState.toggleRowExpansion(row.id)}
                                                    icon={isExpanded ? 'minus' : 'plus'}
                                                    disabled={!hasDetails}
                                                    data-test='expand-button'
                                                />
                                            </TableCell>
                                            {columns.includes('date') && (
                                                <TableCell
                                                    className='h-14'
                                                    data-test='audit-date'
                                                >
                                                    {DateTime.from(row.at)?.format('DD/MM/YYYY HH:mm:ss') ?? '-'}
                                                </TableCell>
                                            )}
                                            {columns.includes('operation') && (
                                                <TableCell
                                                    className='h-14'
                                                    data-test='audit-op'
                                                >
                                                    {row.op}
                                                </TableCell>
                                            )}
                                            {columns.includes('actor') && (
                                                <TableCell
                                                    className='h-14'
                                                    data-test='audit-actor'
                                                >
                                                    {renderWithLoading(state.getActorDisplayName(row.actor_id))}
                                                </TableCell>
                                            )}
                                            {columns.includes('entry_point') && (
                                                <TableCell
                                                    className='h-14'
                                                    data-test='audit-entrypoint'
                                                >
                                                    {row.entrypoint == null
                                                        ? '-'
                                                        : renderWithLoading(
                                                              state.resolveActorIdsInText(row.entrypoint)
                                                          )}
                                                </TableCell>
                                            )}
                                            {columns.includes('reason') && (
                                                <TableCell
                                                    className='h-14'
                                                    data-test='audit-reason'
                                                >
                                                    {row.reason == null
                                                        ? '-'
                                                        : renderReasonWithTooltips(row.reason, state)}
                                                </TableCell>
                                            )}
                                        </TableRow>
                                        {isExpanded ? (
                                            <TableRow
                                                className={cn(
                                                    rowClass,
                                                    'overflow-hidden border-border border-solid border-b border-t-0 border-l-0 border-r-0'
                                                )}
                                            >
                                                <TableCell
                                                    colSpan={columnNumber}
                                                    className='py-3 last:px-4 first:px-4'
                                                >
                                                    <div
                                                        className='pl-10 mt-0 mb-3'
                                                        data-test='audit-details'
                                                    >
                                                        {feature === 'GamstopRequestHistory'
                                                            ? formatJsonRecord(row.after)
                                                            : formatJsonDiff(
                                                                  row.before,
                                                                  row.after,
                                                                  row.cols_changed ?? undefined
                                                              )}
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ) : null}
                                    </React.Fragment>
                                );
                            })}
                        </TableBody>
                    </Table>
                    {state.store.hasMore ? (
                        <div className='flex justify-center py-4'>
                            <Button
                                variant='outline'
                                onClick={state.store.loadMore}
                                disabled={state.store.isLoadingMore}
                            >
                                {state.store.isLoadingMore ? 'Loading...' : 'Show more'}
                            </Button>
                        </div>
                    ) : null}
                </div>
            )}
        </AuditPanelWrapper>
    );
});

interface ComplianceAuditContentPropsType {
    view: ComplianceAuditViewType;
}

export const ComplianceAuditContent = observer(
    'SelfExclusionAuditContent',
    ({ view }: ComplianceAuditContentPropsType) => {
        const { routerBackoffice } = useAppStateContext();

        const customerId = routerBackoffice.getValueString('customers');
        const numberedCustomerId = parseInt(customerId?.toString() ?? '', 10);

        if (customerId === null || isNaN(numberedCustomerId)) {
            return null;
        }

        return (
            <ComplianceAuditInner
                query={{ kind: 'account', feature: view, accountId: numberedCustomerId }}
                columns={AUDIT_COLUMNS[view]}
            />
        );
    }
);

interface ComplianceConfigAuditContentPropsType {
    feature: 'SourceOfFunds' | 'SelfExclusion';
}

export const ComplianceConfigAuditContent = observer(
    'ComplianceConfigAuditContent',
    ({ feature }: ComplianceConfigAuditContentPropsType) => {
        return (
            <ComplianceAuditInner
                query={{ kind: 'config', feature }}
                columns={AUDIT_COLUMNS[feature]}
            />
        );
    }
);
